import React, { useCallback, useState } from "react";
import { TextInputWithAction } from "./TextInputWithAction";
import { checkForDuplicate } from "../../utils/checkForDuplicate";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";
import { icons, colors2, UI_CAPTION_SEMIBOLD_DF } from "oolib";
import { AnimatePresence, motion } from "framer-motion";
import styled from "styled-components";

const { ListChecks, ArrowElbowDownLeft } = icons;

const StyledContainer = styled.div`
  margin: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;

  background: ${colors2.grey5};
  border-radius: 0.6rem;
`;

const StyledHintContainer = styled(motion.div)`
  overflow: hidden;
`;

const StyledContent = styled.div`
  padding: 0.9rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const StyledContentLine = styled.div`
  display: flex;
  gap: 0.4rem;
`;

/**
 * @param {Object} props
 * @param {string} props.value - Controlled input value
 * @param {Function} props.onChange - Callback when input value changes (id, value)
 * @param {Function} props.onAction - Callback when action button is clicked (tagValue)
 * @param {string} props.placeholder - Input placeholder text
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.id - Input ID
 * @param {string} props.actionButtonIcon - Icon for the action button
 * @param {string} props.actionButtonLabel - Label for the action button
 * @param {Object} props.inputProps - Additional props to pass to the input element
 * @param {string} props.category - Tag category for duplicate checking
 * @param {Array} props.localTagsData - Local tags data for duplicate checking
 * @param {Object} props.tagTitles - Tag titles mapping
 * @param {Object} props.restProps - Additional props for the container
 */
export const TagsManagerTextInput = ({
  value = "",
  onChange,
  onAction,
  placeholder = "Enter text (comma-separated for multiple)...",
  disabled = false,
  id = "tags-manager-text-input",
  actionButtonIcon = "Plus",
  actionButtonLabel = "Add",
  inputProps = {},
  category,
  localTagsData,
  tagTitles,
  containerStyle,
  ...restProps
}) => {
  const [isFocused, setIsFocused] = useState(false);

  // Parse comma-separated values and handle edge cases
  const parseCommaSeparatedValues = useCallback((input) => {
    if (!input || typeof input !== "string") return [];

    // Split by comma and handle various edge cases
    return input
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0) // Remove empty items
      .filter((item) => item.length <= 100) // Limit tag length to prevent abuse
      .filter((item, index, array) => array.indexOf(item) === index) // Remove duplicates
      .slice(0, 20); // Limit to max 20 tags per input to prevent abuse
  }, []);

  // Check for duplicates in comma-separated values and return single message
  const checkDuplicatesInInput = useCallback(
    (input) => {
      if (!input || !category || !localTagsData || !tagTitles) return null;

      // First, get all values from the input (including duplicates within input)
      const allInputValues = input
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

      // Check for duplicates within the input itself
      const inputDuplicates = [];
      const seenInInput = new Set();

      allInputValues.forEach((value) => {
        if (seenInInput.has(value.toLowerCase())) {
          inputDuplicates.push(value);
        } else {
          seenInInput.add(value.toLowerCase());
        }
      });

      // Check for duplicates against existing tags
      const parsedValues = parseCommaSeparatedValues(input);
      const existingDuplicates = [];

      parsedValues.forEach((value) => {
        const isDuplicate = checkForDuplicate(
          value,
          category,
          null, // No excludeId for new tags
          localTagsData
        );

        if (isDuplicate) {
          existingDuplicates.push(value);
        }
      });

      // Build error message combining both types of duplicates
      const errorMessages = [];

      if (inputDuplicates.length > 0) {
        const inputDuplicateText =
          inputDuplicates.length === 1
            ? `"${inputDuplicates[0]}"`
            : `"${inputDuplicates.join('", "')}"`;
        errorMessages.push(`Duplicate entry: ${inputDuplicateText}`);
      }

      if (existingDuplicates.length > 0) {
        const existingDuplicateText =
          existingDuplicates.length === 1
            ? `"${existingDuplicates[0]}"`
            : `"${existingDuplicates.join('", "')}"`;
        errorMessages.push(
          `A ${
            tagTitles[category]?.slice(0, -1) || category
          } with this name already exists: ${existingDuplicateText}`
        );
      }

      // Return combined error message if there are any duplicates
      return errorMessages.length > 0 ? errorMessages.join(". ") : null;
    },
    [parseCommaSeparatedValues, category, localTagsData, tagTitles]
  );

  // Handle action with comma-separated parsing
  const handleAction = useCallback(
    (inputValue) => {
      if (onAction && inputValue.trim()) {
        const parsedValues = parseCommaSeparatedValues(inputValue);

        if (parsedValues.length > 0) {
          // If multiple values, call onAction for each
          if (parsedValues.length === 1) {
            // Single value - call onAction once
            onAction(parsedValues[0]);
          } else {
            // Multiple values - call onAction for each
            parsedValues.forEach((value) => {
              onAction(value);
            });
          }
          setIsFocused(false);
        }
      }
    },
    [onAction, parseCommaSeparatedValues]
  );

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <StyledContainer style={containerStyle}>
      <TextInputWithAction
        value={value}
        onChange={onChange}
        onAction={handleAction}
        placeholder={placeholder}
        disabled={disabled}
        id={id}
        actionButtonIcon={actionButtonIcon}
        actionButtonLabel={actionButtonLabel}
        inputProps={inputProps}
        validateInput={checkDuplicatesInInput}
        clearOnAction={true}
        showError={true}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...restProps}
      />
      <AnimatePresence initial={false}>
        <StyledHintContainer
          animate={{ height: isFocused ? "auto" : 0 }}
          transition={{ type: "spring", bounce: 0, duration: 0.3 }}
        >
          <StyledContent>
            <StyledContentLine>
              <ArrowElbowDownLeft size={14} style={{ flexShrink: 0 }} />
              <UI_CAPTION_SEMIBOLD_DF>
                {
                  SM_UIContent.SM_ThemesManager.addInputHelpText[category]
                    .helpText1
                }
              </UI_CAPTION_SEMIBOLD_DF>
            </StyledContentLine>
            <StyledContentLine>
              <ListChecks size={14} style={{ flexShrink: 0 }} />
              <UI_CAPTION_SEMIBOLD_DF>
                {
                  SM_UIContent.SM_ThemesManager.addInputHelpText[category]
                    .helpText2
                }
              </UI_CAPTION_SEMIBOLD_DF>
            </StyledContentLine>
          </StyledContent>
        </StyledHintContainer>
      </AnimatePresence>
    </StyledContainer>
  );
};
